import React, { useContext, useEffect } from 'react';

import { Building2, DollarSign, User } from 'lucide-react';

import { getQueryKey, loadIdeaImpact } from 'common/actions/ideaImpact';
import { loadConfiguration } from 'common/actions/ideasConfiguration';
import { RevenueTimeframes } from 'common/company/RevenueHelpers';
import { CompanyContext } from 'common/containers/CompanyContainer';
import connect from 'common/core/connect';
import IdeaStatus from 'common/ideas/IdeaStatusPill';
import getIdeaURL from 'common/ideas/utils/getIdeaURL';
import { P } from 'common/ui/Text';
import abbreviateNumber from 'common/util/abbreviateNumber';
import numberWithCommas from 'common/util/numberWithCommas';
import styles from 'css-module/components/subdomain/admin/_AdminIdeaPostSidebarSection.module.scss';

import AdminSidebarSection from './AdminSidebarSection';

import type { Post } from 'common/api/resources/posts';
import type { IdeaImpactQueriesState } from 'common/reducers/ideaImpact';
import type { IdeasConfigurationState } from 'common/reducers/ideasConfiguration';

type Props = {
  post: Post;
  ideasConfiguration: IdeasConfigurationState;
  ideaImpact: IdeaImpactQueriesState;
  loadConfiguration: () => void;
  loadIdeaImpact: (queryParams: { ideaID: string }) => void;
};

const AdminIdeaPostSidebarSection = ({
  post,
  ideasConfiguration,
  ideaImpact,
  loadConfiguration,
  loadIdeaImpact,
}: Props) => {
  const company = useContext(CompanyContext);
  const { idea } = post;

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  useEffect(() => {
    if (idea) {
      loadIdeaImpact({ ideaID: idea._id });
    }
  }, [loadIdeaImpact, idea]);

  if (!idea) {
    return null;
  }

  const ideaStatuses = ideasConfiguration.data?.statuses ?? null;
  const status = ideaStatuses?.find((status) => status._id === idea.statusID);

  const queryKey = getQueryKey({ ideaID: idea._id });
  const impact = ideaImpact[queryKey];
  const isLoading = !impact || impact.loading;

  const users = impact?.items?.find((item) => item.name === 'users')?.value;
  const companies = impact?.items?.find((item) => item.name === 'companies')?.value;
  const monthlySpend = impact?.items?.find((item) => item.name === 'monthlySpend')?.value;

  let spend: number | string = 0;
  if (monthlySpend) {
    spend = company.revenueTimeframe === RevenueTimeframes.mrr ? monthlySpend : monthlySpend * 12;
    spend = abbreviateNumber(spend);
  }

  return (
    <AdminSidebarSection title="Idea">
      <a href={getIdeaURL(idea)} target="_blank" className={styles.postLinkedIdea} rel="noopener">
        <P fontWeight="medium" variant="bodyMd">
          {idea.title}
        </P>
        <div className={styles.postLinkedIdeaContent}>
          <div>{status && <IdeaStatus status={status} />}</div>
          <div className={styles.postLinkedIdeaCounts}>
            <div className={styles.postLinkedIdeaCount}>
              <User size={14} />
              {isLoading ? '...' : numberWithCommas(users ?? 0)}
            </div>
            <div className={styles.postLinkedIdeaCount}>
              <Building2 size={14} />
              {isLoading ? '...' : numberWithCommas(companies ?? 0)}
            </div>
            <div className={styles.postLinkedIdeaCount}>
              <DollarSign size={14} />
              {isLoading ? '...' : spend}
            </div>
          </div>
        </div>
      </a>
    </AdminSidebarSection>
  );
};

export default connect(
  ({
    ideasConfiguration,
    ideaImpact,
  }: {
    ideasConfiguration: IdeasConfigurationState;
    ideaImpact: IdeaImpactQueriesState;
  }) => ({
    ideasConfiguration,
    ideaImpact,
  }),
  {
    loadConfiguration,
    loadIdeaImpact,
  },
)(AdminIdeaPostSidebarSection);
